import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Pressable,
  Modal,
  ActivityIndicator,
} from "react-native";
import {
  format,
  parse,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
} from "date-fns";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import { RouteProp, useRoute } from "@react-navigation/native";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { ConfirmDialog } from "../../../shared/ui/ConfirmDialog";
import { Input } from "../../../shared/ui/FormElements";
import { Flash } from "../../../shared/utils/Flash";
import { UpGradationService } from "../../../services/UpGradationService";
import { AuthStackParamList } from "../../../navigation/navigationTypes";
import { resolveHistoryRange } from "../../../shared/utils/historyNavigation";
import { useAuth } from "../../../../context/AuthContext";

/* ================= THEME ================= */

const theme = {
  primary: "#F97316",
  primarySoft: "rgba(249,115,22,0.12)",
  primaryBorder: "rgba(249,115,22,0.35)",
  textDark: "#4A2311",
  textMedium: "rgba(26,71,49,0.65)",
  borderLight: "rgba(0,0,0,0.06)",
};

/* safe parser */

const parseHistoryDate = (s: string) =>
  parse(s, "dd-MM-yyyy", new Date());

type FilterType = "Weekly" | "Monthly" | "Quarterly" | "Yearly" | "Custom";
type UpGradationHistoryRouteProp = RouteProp<AuthStackParamList, "UpGradationHistory">;

export function UpGradationHistoryScreen() {
  const { user } = useAuth();
  const route = useRoute<UpGradationHistoryRouteProp>();
  const initialRange = resolveHistoryRange(route.params);
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [summary, setSummary] = useState({ isSelf: true });
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const [editing, setEditing] = useState<any | null>(null);

  const [oneSeating, setOneSeating] = useState("");
  const [withoutOffence, setWithoutOffence] = useState("");
  const [dayStart, setDayStart] = useState("");
  const [prayers, setPrayers] = useState("");
  const [sravan, setSravan] = useState("");
  const [comments, setComments] = useState("");

  const [filterType, setFilterType] = useState<FilterType>(
    initialRange.hasNavigationDates ? "Custom" : "Monthly"
  );

  const effectiveUserId = useMemo(() => {
    return initialRange.devoteeId || user?.userId || "";
  }, [initialRange.devoteeId, user?.userId]);

  const [fromDate, setFromDate] = useState(initialRange.fromDate);
  const [toDate, setToDate] = useState(initialRange.toDate);

  /* ================= LOAD HISTORY ================= */

  const loadHistory = useCallback(async () => {
    try {
      if (!effectiveUserId) return;
      setLoading(true);

      const res = await UpGradationService.history({
        userId: effectiveUserId,
        fromDate,
        toDate,
      });

      if (res?.status === 200) {
        setEntries(res.data.records || []);
        setSummary(res.data.response);
      }
    } catch {
      Flash.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  /* ================= FILTER ================= */

  const applyFilter = (type: FilterType) => {
    setFilterType(type);
    const now = new Date();

    let start = startOfMonth(now);
    let end = endOfMonth(now);

    if (type === "Weekly") {
      start = startOfWeek(now);
      end = endOfWeek(now);
    } else if (type === "Quarterly") {
      start = startOfQuarter(now);
      end = endOfQuarter(now);
    } else if (type === "Yearly") {
      start = startOfYear(now);
      end = endOfYear(now);
    }

    setFromDate(format(start, "yyyy-MM-dd"));
    setToDate(format(end, "yyyy-MM-dd"));
  };

  const openPicker = (mode: "from" | "to") => {
    DateTimePickerAndroid.open({
      value: new Date(mode === "from" ? fromDate : toDate),
      mode: "date",
      onChange: (e, selectedDate) => {
        if (e.type !== "set" || !selectedDate) return;

        const formatted = format(selectedDate, "yyyy-MM-dd");
        mode === "from" ? setFromDate(formatted) : setToDate(formatted);
        setFilterType("Custom");
      },
    });
  };

  /* ================= DELETE ================= */

  const deleteEntry = (id: string) => {
    setPendingDeleteId(id);
  };

  /* ================= EDIT ================= */

  const openEdit = async (item: any) => {
    const res = await UpGradationService.get(item.data.upGradationId);
    if (res?.status !== 200) return;

    const d = res.data[0];
    setEditing(d);
    setOneSeating(String(d.oneSeating || ""));
    setWithoutOffence(String(d.withoutOffence || ""));
    setDayStart(String(d.dayStart || ""));
    setPrayers(String(d.inJapaPrayers || ""));
    setSravan(String(d.japaSravan || ""));
    setComments(d.comments || "");
  };

  const updateEntry = async () => {
    if (!editing) return;

    const payload = {
      upGradationId: editing.upGradationId,
      date: format(new Date(editing.date), "yyyy-MM-dd"),
      oneSeating: +oneSeating || 0,
      withoutOffence: +withoutOffence || 0,
      dayStart: +dayStart || 0,
      inJapaPrayers: +prayers || 0,
      japaSravan: +sravan || 0,
      comments,
      marks:
        (+oneSeating || 0) +
        (+withoutOffence || 0) +
        (+dayStart || 0) +
        (+prayers || 0) +
        (+sravan || 0),
    };

    const res = await UpGradationService.update(payload);

    if (res?.status === 200) {
      Flash.success("Success!", "Upgradation Updated Successfully!");
      setEditing(null);
      loadHistory();
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadHistory();
    } finally {
      setRefreshing(false);
    }
  };

  /* ================= UI ================= */

  return (
    <DivineBackground overlayOpacity={0.95}>
      <Header title="History & Analytics" showBack showHome />

      <ScrollView
        contentContainerStyle={{ padding: 14 }}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={["#F97316"]}
            tintColor="#F97316"
          />
        }
      >

        {/* FILTER CARD */}
        <View style={styles.filterCard}>
          <View style={styles.filterHeader}>
            <Text style={{ fontSize: 16 }}>🔎</Text>
            <Text style={styles.filterTitle}>Filter History</Text>
          </View>

          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {["Weekly", "Monthly", "Quarterly", "Yearly"].map((x) => (
              <Pressable
                key={x}
                onPress={() => applyFilter(x as FilterType)}
                style={[
                  styles.filterChip,
                  filterType === x && styles.filterChipActive,
                ]}
              >
                <Text
                  style={{
                    color: filterType === x ? "#fff" : theme.primary,
                    fontWeight: "700",
                  }}
                >
                  {x}
                </Text>
              </Pressable>
            ))}
          </ScrollView>

          <View style={styles.dateRow}>
            <Pressable style={styles.dateInput} onPress={() => openPicker("from")}>
              <Text>📅 {format(new Date(fromDate), "dd-MM-yyyy")}</Text>
            </Pressable>

            <Pressable style={styles.dateInput} onPress={() => openPicker("to")}>
              <Text>📅 {format(new Date(toDate), "dd-MM-yyyy")}</Text>
            </Pressable>
          </View>
        </View>

        {/* LIST */}
        {loading ? (
          <View style={styles.emptyBox}>
            <ActivityIndicator />
            <Text style={styles.emptyText}>Loading...</Text>
          </View>
        ) : entries.length === 0 ? (
          <View style={styles.emptyBox}>
            <Text style={styles.emptyText}>No records found for this period.</Text>
          </View>
        ) : (
          entries.map((e, i) => {
            const dt = parseHistoryDate(e.date);

            return (
              <View key={i} style={styles.card}>
                <View style={styles.topRow}>
                  <View>
                    <Text style={styles.date}>
                      {format(dt, "dd MMM yyyy")}
                    </Text>
                    <Text style={styles.day}>
                      {format(dt, "EEEE")}
                    </Text>
                  </View>

                  <View style={{ alignItems: "flex-end" }}>
                    <Text style={styles.marks}>{e.data.marks}</Text>
                    <Text style={styles.marksLabel}>Marks</Text>
                  </View>
                </View>

                <View style={styles.badgeRow}>
                  {[
                    ["1-Seating", e.data.oneSeating],
                    ["No-Offence", e.data.withoutOffence],
                    ["Day-Start", e.data.dayStart],
                    ["Prayers", e.data.inJapaPrayers],
                    ["Sravan", e.data.japaSravan],
                  ].map(([label, val]: any) => (
                    <View key={label} style={styles.badge}>
                      <Text style={styles.badgeText}>
                        {label}: {val}
                      </Text>
                    </View>
                  ))}
                </View>

                {e.data.comments ? (
                  <Text style={styles.comment}>{e.data.comments}</Text>
                ) : null}

                {summary.isSelf && (
                  <View style={styles.actions}>
                    {e.data.isEditable ? (
                      <Pressable onPress={() => openEdit(e)} style={styles.iconBtn}>
                        <Text style={styles.iconBtnText}>✏️</Text>
                      </Pressable>
                    ) : (
                      <View style={[styles.iconBtn, styles.iconBtnPlaceholder]} />
                    )}
                    <Pressable onPress={() => deleteEntry(e.data.upGradationId)} style={[styles.iconBtn, styles.iconBtnDanger]}>
                      <Text style={[styles.iconBtnText, { color: "#DC2626" }]}>🗑️</Text>
                    </Pressable>
                  </View>
                )}
              </View>
            );
          })
        )}
      </ScrollView>

      {/* EDIT MODAL (unchanged) */}
      <Modal visible={!!editing} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Edit Entry</Text>
              <Pressable onPress={() => setEditing(null)}>
                <Text style={{ fontSize: 18 }}>✕</Text>
              </Pressable>
            </View>

            {editing && (
              <Text style={styles.modalDate}>
                Date: {format(new Date(editing.date), "dd MMMM yyyy")}
              </Text>
            )}

            <ScrollView style={{ maxHeight: 360 }}>
              <Input label="One Seating Japa" value={oneSeating} keyboardType="numeric" onChangeText={setOneSeating} />
              <Input label="Without Offence Japa" value={withoutOffence} keyboardType="numeric" onChangeText={setWithoutOffence} />
              <Input label="Day Start Japa" value={dayStart} keyboardType="numeric" onChangeText={setDayStart} />
              <Input label="In Japa Prayers" value={prayers} keyboardType="numeric" onChangeText={setPrayers} />
              <Input label="Japa Sravan" value={sravan} keyboardType="numeric" onChangeText={setSravan} />
              <Input label="Comments" value={comments} onChangeText={setComments} />
            </ScrollView>

            <View style={styles.modalButtons}>
              <Pressable style={styles.cancelBtn} onPress={() => setEditing(null)}>
                <Text style={styles.cancelText}>Cancel</Text>
              </Pressable>

              <Pressable style={styles.updateBtn} onPress={updateEntry}>
                <Text style={styles.updateText}>Update</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
      <ConfirmDialog
        visible={!!pendingDeleteId}
        title="Delete Entry"
        message="Are you sure you want to delete?"
        confirmText="Delete"
        destructive
        onCancel={() => setPendingDeleteId(null)}
        onConfirm={async () => {
          if (!pendingDeleteId) return;
          const res = await UpGradationService.delete(pendingDeleteId);
          if (res?.status === 200) {
            Flash.success("Success!", "Upgradation Deleted Successfully!");
            setPendingDeleteId(null);
            loadHistory();
          } else {
            Flash.error("Delete failed");
          }
        }}
      />
    </DivineBackground>
  );
}

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  filterCard: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 14,
    marginBottom: 12,
  },
  filterHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 8,
  },
  filterTitle: { fontWeight: "800", color: theme.textDark },
  filterChip: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: theme.primarySoft,
    marginRight: 8,
  },
  filterChipActive: {
    backgroundColor: theme.primary,
  },
  dateRow: {
    flexDirection: "row",
    gap: 10,
    marginTop: 10,
  },
  dateInput: {
    flex: 1,
    padding: 10,
    borderRadius: 10,
    backgroundColor: "#F3F4F6",
    alignItems: "center",
  },
  emptyBox: {
    backgroundColor: "rgba(255,255,255,0.55)",
    borderRadius: 16,
    paddingVertical: 28,
    alignItems: "center",
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
  },
  emptyText: { marginTop: 8, color: "rgba(74,35,17,0.55)", fontWeight: "800" },

  card: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 14,
    marginBottom: 12,
  },
  topRow: { flexDirection: "row", justifyContent: "space-between" },
  date: { fontWeight: "900", color: theme.textDark },
  day: { fontSize: 12, color: "#6B7280" },
  marks: { fontSize: 20, fontWeight: "900", color: "#3B82F6" },
  marksLabel: { fontSize: 10, color: "#9CA3AF" },
  badgeRow: { flexDirection: "row", flexWrap: "wrap", gap: 6, marginTop: 8 },
  badge: {
    backgroundColor: "#EFF6FF",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  badgeText: { fontSize: 11 },
  comment: { marginTop: 8, fontSize: 12, color: "#374151" },
  actions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 14,
    marginTop: 10,
  },

  iconBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(37,99,235,0.10)",
  },
  iconBtnDanger: { backgroundColor: "rgba(220,38,38,0.10)" },
  iconBtnPlaceholder: { opacity: 0 },
  iconBtnText: { fontSize: 14, fontWeight: "900", color: "#1D4ED8" },

  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center",
    padding: 20,
  },
  modalCard: {
    backgroundColor: "#fff",
    borderRadius: 20,
    padding: 20,
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  modalTitle: { fontSize: 18, fontWeight: "800" },
  modalDate: { marginTop: 6, marginBottom: 14, color: "#6B7280" },
  modalButtons: { flexDirection: "row", gap: 10, marginTop: 16 },
  cancelBtn: {
    flex: 1,
    borderWidth: 1,
    borderColor: theme.primary,
    borderRadius: 10,
    height: 46,
    alignItems: "center",
    justifyContent: "center",
  },
  cancelText: { color: theme.primary, fontWeight: "700" },
  updateBtn: {
    flex: 1,
    backgroundColor: theme.primary,
    borderRadius: 10,
    height: 46,
    alignItems: "center",
    justifyContent: "center",
  },
  updateText: { color: "#fff", fontWeight: "800" },
});
